// Shopify Storefront API client
const DOMAIN = process.env.REACT_APP_SHOPIFY_DOMAIN;
const TOKEN = process.env.REACT_APP_SHOPIFY_TOKEN;
const API_VERSION = '2024-10';

export const SHOPIFY_ENABLED = Boolean(DOMAIN && TOKEN);

const endpoint = () => `https://${DOMAIN}/api/${API_VERSION}/graphql.json`;

export async function shopifyFetch(query, variables = {}) {
  if (!SHOPIFY_ENABLED) throw new Error('Shopify not configured');
  const res = await fetch(endpoint(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': TOKEN,
      Accept: 'application/json',
    },
    body: JSON.stringify({ query, variables }),
  });
  const json = await res.json();
  if (json.errors) throw new Error(json.errors[0]?.message || 'Shopify error');
  return json.data;
}

const PRODUCT_FIELDS = `
  id
  handle
  title
  description
  tags
  vendor
  productType
  featuredImage { url altText }
  images(first: 6) { edges { node { url altText } } }
  priceRange { minVariantPrice { amount currencyCode } }
  variants(first: 10) { edges { node { id title availableForSale price { amount currencyCode } selectedOptions { name value } } } }
`;

export async function fetchProducts(first = 50) {
  const data = await shopifyFetch(`
    query Products($first: Int!) {
      products(first: $first, sortKey: BEST_SELLING) {
        edges { node { ${PRODUCT_FIELDS} } }
      }
    }
  `, { first });
  return data.products.edges.map((e) => normalize(e.node));
}

export async function fetchProductByHandle(handle) {
  const data = await shopifyFetch(`
    query ProductByHandle($handle: String!) {
      productByHandle(handle: $handle) { ${PRODUCT_FIELDS} }
    }
  `, { handle });
  return data.productByHandle ? normalize(data.productByHandle) : null;
}

function normalize(n) {
  const tags = (n.tags || []).map((t) => t.toLowerCase());
  const typeMap = { skincare: 'skincare', 'beauty & grooming': 'grooming', grooming: 'grooming', jewelry: 'jewelry', 'self-care': 'selfcare', selfcare: 'selfcare' };
  const pt = (n.productType || '').toLowerCase();
  const category = typeMap[pt] || tags.find((t) => typeMap[t]) || 'skincare';
  const gender = tags.includes('men') ? 'men' : tags.includes('women') ? 'women' : 'unisex';
  const v = n.variants?.edges?.[0]?.node;
  const amt = parseFloat(v?.price?.amount || n.priceRange?.minVariantPrice?.amount || '0');
  return {
    id: n.handle,
    shopifyId: n.id,
    variantId: v?.id,
    title: n.title,
    handle: n.handle,
    description: n.description,
    image: n.featuredImage?.url || n.images?.edges?.[0]?.node?.url,
    images: (n.images?.edges || []).map((e) => e.node.url),
    price: amt,
    currency: v?.price?.currencyCode || 'ZAR',
    category,
    gender,
    tagline: (n.description || '').split('\n')[0]?.slice(0, 120) || '',
    ingredients: '',
    size: v?.title && v.title !== 'Default Title' ? v.title : '',
    variants: (n.variants?.edges || []).map((e) => e.node),
    isShopify: true,
  };
}

// Cart API
const CART_FIELDS = `
  id
  checkoutUrl
  totalQuantity
  cost { subtotalAmount { amount currencyCode } totalAmount { amount currencyCode } }
  lines(first: 50) {
    edges { node { id quantity merchandise { ... on ProductVariant { id title price { amount currencyCode } product { title handle featuredImage { url } } } } } }
  }
`;

export async function cartCreate(lines = []) {
  const data = await shopifyFetch(`
    mutation CartCreate($input: CartInput!) {
      cartCreate(input: $input) { cart { ${CART_FIELDS} } userErrors { message } }
    }
  `, { input: { lines } });
  return data.cartCreate.cart;
}

export async function cartLinesAdd(cartId, lines) {
  const data = await shopifyFetch(`
    mutation Add($cartId: ID!, $lines: [CartLineInput!]!) {
      cartLinesAdd(cartId: $cartId, lines: $lines) { cart { ${CART_FIELDS} } userErrors { message } }
    }
  `, { cartId, lines });
  return data.cartLinesAdd.cart;
}

export async function cartLinesUpdate(cartId, lines) {
  const data = await shopifyFetch(`
    mutation Upd($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
      cartLinesUpdate(cartId: $cartId, lines: $lines) { cart { ${CART_FIELDS} } userErrors { message } }
    }
  `, { cartId, lines });
  return data.cartLinesUpdate.cart;
}

export async function cartLinesRemove(cartId, lineIds) {
  const data = await shopifyFetch(`
    mutation Rem($cartId: ID!, $lineIds: [ID!]!) {
      cartLinesRemove(cartId: $cartId, lineIds: $lineIds) { cart { ${CART_FIELDS} } userErrors { message } }
    }
  `, { cartId, lineIds });
  return data.cartLinesRemove.cart;
}

export async function fetchCart(cartId) {
  const data = await shopifyFetch(`query C($id: ID!){ cart(id:$id){ ${CART_FIELDS} } }`, { id: cartId });
  return data.cart;
}
